'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import type { Building, SearchItem } from '@repo/api';
import { useStore } from '@/providers/StoreProvider';
import { buildingUrl, listingsUrl } from '@/utils/routes';

const NYC_CITY_ID = 'nyc';

/**
 * Navigates after a user picks an address from the "Read reviews" building search
 * (autocomplete suggestions, the recent-searches dropdown, and the recent-search cards).
 *
 * - NYC address -> `/listings/...` with that address selected as the location filter.
 * - Any other city (or an NYC result missing the slugs the listings filter needs)
 *   -> the building page, exactly as before.
 */
export function useNavigateToSearchedBuilding() {
  const router = useRouter();
  const resetAllFilters = useStore(s => s.listingsFilters.resetAllFilters);
  const setBuilding = useStore(s => s.listingsFilters.setBuilding);
  const getUrlFilterPayload = useStore(s => s.listingsFilters.getUrlFilterPayload);

  return React.useCallback(
    (item: SearchItem) => {
      const isNyc = item.cityId?.toLowerCase() === NYC_CITY_ID;

      if (isNyc && item.slug && item.buildingSlug) {
        // Start from a clean filter set so stale persisted filters (price, beds, neighborhoods,
        // "verified only", ...) can't hide the listings of the address the user just picked.
        resetAllFilters();
        setBuilding({
          addressSlug: item.slug,
          slug: item.buildingSlug,
          address: item.streetAddress || item.fullAddress,
        });
        router.push(listingsUrl(getUrlFilterPayload()));
        return;
      }

      router.push(
        buildingUrl({
          slug: item.buildingSlug,
          primaryAddress: { cityId: item.cityId, slug: item.slug },
          neighborhood: { city: item.cityId },
        } as unknown as Building),
      );
    },
    [router, resetAllFilters, setBuilding, getUrlFilterPayload],
  );
}

export default useNavigateToSearchedBuilding;
